import React from "react";
import { motion } from "framer-motion";
import { Feather, Sprout } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

export default function BrandMeaning() {
  const { t } = useT();
  const tags = [
    t("brand.tag.support"),
    t("brand.tag.education"),
    t("brand.tag.compassion"),
    t("brand.tag.empowerment"),
  ];
  return (
    <section
      id="brand"
      data-testid="brand-meaning-section"
      className="py-24 sm:py-32 bg-[#3F5043] text-[#F8F7F3] relative overflow-hidden"
    >
      {/* Decorative oversized type behind */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center select-none"
      >
        <span className="font-serif italic text-[28vw] sm:text-[22vw] leading-none text-[#F8F7F3]/[0.04] tracking-tighter">
          soarlife
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="text-xs uppercase tracking-[0.28em] text-[#AE8F78] font-semibold mb-5">
            {t("brand.eyebrow")}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02]">
            {t("brand.title.a")}{" "}
            <em className="text-[#AE8F78]">{t("brand.title.em")}</em>{" "}
            {t("brand.title.b")}
          </h2>
        </motion.div>

        {/* Word breakdown */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="relative"
          >
            <div className="flex items-baseline gap-5 mb-5">
              <span className="font-serif italic text-7xl sm:text-8xl text-[#F8F7F3] leading-none">
                Soar
              </span>
              <Feather
                size={28}
                strokeWidth={1.3}
                className="text-[#AE8F78] -mt-2"
              />
            </div>
            <div className="h-px w-16 bg-[#AE8F78] mb-6" />
            <p className="text-base sm:text-lg leading-relaxed text-[#F8F7F3]/85 max-w-md">
              {t("brand.soar.text")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.12 }}
            className="relative md:pl-10 md:border-l md:border-[#F8F7F3]/15"
          >
            <div className="flex items-baseline gap-5 mb-5">
              <span className="font-serif italic text-7xl sm:text-8xl text-[#F8F7F3] leading-none">
                Life
              </span>
              <Sprout
                size={28}
                strokeWidth={1.3}
                className="text-[#AE8F78] -mt-2"
              />
            </div>
            <div className="h-px w-16 bg-[#AE8F78] mb-6" />
            <p className="text-base sm:text-lg leading-relaxed text-[#F8F7F3]/85 max-w-md">
              {t("brand.life.text")}
            </p>
          </motion.div>
        </div>

        {/* Closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-20 sm:mt-24 max-w-4xl"
        >
          <div className="flex items-start gap-5">
            <span className="font-serif text-6xl text-[#AE8F78] leading-none mt-2">
              “
            </span>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.2] italic text-[#F8F7F3]">
              {t("brand.closing.a")}{" "}
              <em className="not-italic text-[#AE8F78]">Soarlife</em>{" "}
              {t("brand.closing.b")}
            </p>
          </div>

          <div className="mt-12 flex flex-wrap gap-2">
            {tags.map((w) => (
              <span
                key={w}
                className="text-xs uppercase tracking-[0.25em] font-semibold px-5 py-2.5 rounded-full border border-[#F8F7F3]/25 text-[#F8F7F3]/85"
              >
                {w}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
